import { useState, useEffect, useRef, ReactNode } from 'react';
import { apiPublic, logClick } from '../api.js';

// Define the structure for the data payload of each result type
interface ProductData {
  id: number
  title: string;
  sku: string;
  image_url?: string;
}

interface ProductCompatData extends ProductData {
  compat: {
    brand: string
    part_number: string
  }
}

interface SearchResultBase {
  result_url: string
  title: string
  relevance: number
}
interface SearchResultProductPart extends SearchResultBase {
  result_type: 'part'
  data: ProductData
}
interface SearchResultProductParentPart extends SearchResultBase {
  result_type: 'parent_part'
  data: ProductData
}
interface SearchResultProductCompat extends SearchResultBase {
  result_type: 'compat'
  data: ProductCompatData
}


// Create a discriminated union for type-safe handling of different search results
type SearchResult = SearchResultProductParentPart | SearchResultProductPart | SearchResultProductCompat;

export function SearchBar({ ptype }: { ptype: string }) {
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'part' | 'keyword'>('part');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [logId, setLogId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Debounce effect for API calls
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    // Set loading to true IMMEDIATELY when typing starts
    setIsLoading(true);

    const endpoint = searchMode === 'part'
      ? `/search-part?q=${encodeURIComponent(query)}`
      : `/search?q=${encodeURIComponent(query)}`;

    const delayDebounceFn = setTimeout(() => {
      apiPublic<SearchResult[]>(endpoint)
        .then(response => {
          setResults(response.data);
          setLogId(response.log_id ?? null);
        })
        .catch(error => {
          console.error("Search API error:", error);
          setResults([]); // Clear results on error
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300); // 300ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [query, searchMode]);

  // Effect to handle clicks outside the search bar to close the results
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchContainerRef]);

  return (
    <div className="superlok-search-container" ref={searchContainerRef}>
      <div className="superlok-search-mode-toggle">
        <label className={`superlok-search-mode-option${searchMode === 'part' ? ' selected' : ''}`}>
          <input
            type="radio"
            name="superlok-search-mode"
            value="part"
            checked={searchMode === 'part'}
            onChange={() => { setSearchMode('part'); setResults([]); }}
          />
          Part #
        </label>
        <label className={`superlok-search-mode-option${searchMode === 'keyword' ? ' selected' : ''}`}>
          <input
            type="radio"
            name="superlok-search-mode"
            value="keyword"
            checked={searchMode === 'keyword'}
            onChange={() => { setSearchMode('keyword'); setResults([]); }}
          />
          Keyword
        </label>
      </div>
      <div className="superlok-search-input-wrapper">
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder="Search by Part# or Keyword..."
          className="superlok-search-input"
        />  
      </div>

      <div className="superlok-search-results-container">

        {isFocused && isLoading && (query.length > 1) && (<div className="superlok-search-spinner"><div></div></div>)}

        {isFocused && !isLoading && (query.length > 1) && (
          <div className="superlok-search-results-modal">
            <div className="superlok-search-results">
              {results.length > 0 ? (
                <ul>
                  {results.map((result, index) => <SearchResult key={index} result={result} logId={logId} />)}
                </ul>
              ) : (
                !isLoading && <div className="superlok-no-results"><div>No results found</div></div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


function SearchResult({ result, logId }: { result: SearchResult, logId: number | null }) {
  const node: ReactNode = (() => {
    switch (result.result_type) {
      case 'parent_part':
        return <SRParentPart result={result} logId={logId} />;
      case 'part':
        return <SRPart result={result} logId={logId} />;
      case 'compat':
        return <SRCompat result={result} logId={logId} />;
    }
  })();

  return (
  <li className={`result result-type-${result.result_type}`}>
    {/*
    <div style={{ fontSize: "0.8rem", margin: "0 0 0 5rem", padding: "0.25rem 0.5rem", background: "#444", color: "white" }}>
      relevance: {result.relevance}
    </div> 
    */}
    {node}
  </li>
  );  
}

function SRParentPart({ result, logId }: { result: SearchResultProductParentPart, logId: number | null }) {
  return (
    <a href={result.result_url} onClick={() => logId && logClick(logId, result.result_type, result.data.id, result.result_url)}>
      {result.data.image_url && (
        <span className="thumbimg">
          <img src={result.data.image_url} alt={result.title} />
        </span>
      )}{" "}
      <span className="sku">{result.title}</span>
    </a>
  )
}

function SRPart({ result, logId }: { result: SearchResultProductPart, logId: number | null }) {
  return (
    <a href={result.result_url} onClick={() => logId && logClick(logId, result.result_type, result.data.id, result.result_url)}>
      {result.data.image_url && (
        <span className="thumbimg">
          <img src={result.data.image_url} alt={result.title} />
        </span>
      )}{" "}
      <span className="sku">{result.data.sku}</span>{" "}
      <span className="title">{result.title}</span>
    </a>
  )
}

function SRCompat({ result, logId }: { result: SearchResultProductCompat, logId: number | null }) {
  return (
    <a href={result.result_url} onClick={() => logId && logClick(logId, result.result_type, result.data.id, result.result_url)}>
      {result.data.image_url && (
        <span className="thumbimg">
          <img src={result.data.image_url} alt={result.title} />
        </span>
      )}{" "}
      <span className="sku">{result.data.sku}</span>{" "}
      <span className="compat">
        <span className="l"></span>
        <span className="b">{result.data.compat?.brand}</span> 
        <span className="pn">{result.data.compat?.part_number}</span>
      </span>
      <span className="title">{result.title}</span>
    </a>
  )
}

